import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  CreditCard,
  Settings,
  BarChart3,
  Plus,
  Users,
} from "lucide-react";
import InvoiceManagement from "@/components/invoices/InvoiceManagement";
import InvoiceTemplateManager from "@/components/invoices/InvoiceTemplateManager";
import PaymentOverview from "@/components/invoices/PaymentOverview";
import { useEnhancedInvoices } from "@/context/EnhancedInvoiceContext";
import { InvoiceTemplate } from "@/types/invoice";
import { useToast } from "@/hooks/use-toast";
import { CreditTestComponent } from "@/components/debug/CreditTestComponent";

const Invoices: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { enhancedInvoices, clients, loading } = useEnhancedInvoices();
  const [activeTab, setActiveTab] = useState("invoices");

  // Mock templates data - replace with actual data from context
  const [templates] = useState<InvoiceTemplate[]>([]);

  // Calculate dashboard metrics
  const totalInvoices = enhancedInvoices.length;
  const totalRevenue = enhancedInvoices.reduce(
    (sum, inv) => sum + inv.totalAmount,
    0
  );
  const paidAmount = enhancedInvoices.reduce(
    (sum, inv) => sum + (inv.paidAmount || 0),
    0
  );
  const outstandingAmount = enhancedInvoices.reduce(
    (sum, inv) => sum + (inv.remainingAmount || 0),
    0
  );
  const overdueInvoices = enhancedInvoices.filter(
    (inv) => inv.isOverdue
  ).length;

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} TND`;
  };

  const handleCreateTemplate = () => {
    toast({
      title: t("common.info"),
      description:
        "Template creation functionality will be implemented in the next task",
    });
  };

  const handleEditTemplate = (template: InvoiceTemplate) => {
    toast({
      title: t("common.info"),
      description: `Template editing for "${template.name}" will be implemented in the next task`,
    });
  };

  const handleDeleteTemplate = (template: InvoiceTemplate) => {
    toast({
      title: t("common.info"),
      description: `Template deletion for "${template.name}" will be implemented in the next task`,
    });
  };

  const handleDuplicateTemplate = (template: InvoiceTemplate) => {
    toast({
      title: t("common.info"),
      description: `Template duplication for "${template.name}" will be implemented in the next task`,
    });
  };

  const handlePreviewTemplate = (template: InvoiceTemplate) => {
    toast({
      title: t("common.info"),
      description: `Template preview for "${template.name}" will be implemented in the next task`,
    });
  };

  const handleSetDefaultTemplate = (template: InvoiceTemplate) => {
    toast({
      title: t("common.info"),
      description: `Setting "${template.name}" as default will be implemented in the next task`,
    });
  };

  const handleRefreshTemplates = () => {
    toast({
      title: t("common.info"),
      description:
        "Template refresh functionality will be implemented in the next task",
    });
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("invoices.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("invoices.subtitle", "Manage invoices, payments, and templates")}
          </p>
        </div>
      </div>

      {/* Dashboard Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.totalInvoices")}
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalInvoices}</div>
            <p className="text-xs text-muted-foreground">
              {t("invoices.totalInvoicesDescription")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.totalRevenue")}
            </CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("invoices.totalRevenueDescription")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.paidAmount")}
            </CardTitle>
            <CreditCard className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(paidAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("invoices.paidAmountDescription")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.outstandingAmount")}
            </CardTitle>
            <CreditCard className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(outstandingAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("invoices.outstandingAmountDescription")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.overdueInvoices")}
            </CardTitle>
            <FileText className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {overdueInvoices}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("invoices.overdueInvoicesDescription")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t("invoices.invoices")}
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            {t("invoices.payments")}
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <InvoiceManagement />
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <PaymentOverview invoices={enhancedInvoices} loading={loading} />
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <InvoiceTemplateManager
            templates={templates}
            loading={loading}
            onCreateTemplate={handleCreateTemplate}
            onEditTemplate={handleEditTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            onDuplicateTemplate={handleDuplicateTemplate}
            onPreviewTemplate={handlePreviewTemplate}
            onSetDefaultTemplate={handleSetDefaultTemplate}
            onRefresh={handleRefreshTemplates}
          />
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t("invoices.clientManagement")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">
                      {t("invoices.totalClients")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t("invoices.clientsCount", { count: clients.length })}
                    </p>
                  </div>
                  <Button
                    onClick={() =>
                      toast({
                        title: t("common.info"),
                        description:
                          "Client management functionality will be implemented in the next task",
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t("invoices.addClient")}
                  </Button>
                </div>

                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("invoices.clientManagementPlaceholder")}</p>
                  <p className="text-sm mt-2">
                    {t("invoices.clientManagementDescription")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debug Tab */}
        <TabsContent value="debug" className="space-y-4">
          <CreditTestComponent />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Invoices;
